<template>
  <tr
    class="group/row hover:bg-beige-0 transition-background border-beige-10 border-t border-solid duration-300 ease-in-out"
  >
    <td class="px-4! py-2!">
      <div class="flex items-center gap-4">
        <span
          ref="thumbnailRef"
          class="rf-thumbnail relative block h-24 w-32 shrink-0 overflow-hidden rounded"
          :class="{ invisible: isPreviewVisible }"
        >
          <RfPromotionThumbnail
            :item="{ ...variant, path_type: path.path_type, device_type: path.device_type }"
            class="[&_img]:h-full [&_img]:w-full [&_img]:object-cover"
          />
          <button
            class="rf-thumbnail--overlay text-body text-white-1 absolute inset-0 flex rounded opacity-0 hover:opacity-100!"
            @click="openPreview"
          >
            <span class="m-auto flex items-center gap-1">
              <RfEyeOpenIcon class="fill-white-1! h-6! w-6!" />
              Preview
            </span>
          </button>
        </span>
        <Teleport to="body">
          <span
            v-if="isPreviewVisible"
            ref="floatingRef"
            class="rf-floating-preview"
            :class="{ 'rf-floating-preview--expanded': isExpanded }"
            :style="floatingStyle"
            @click="closePreview"
          >
            <RfPromotionThumbnail
              :item="{ ...variant, path_type: path.path_type, device_type: path.device_type }"
              class="[&_img]:h-full [&_img]:w-full [&_img]:object-cover"
            />
          </span>
        </Teleport>
        <div class="flex min-w-0 flex-col gap-0.5">
          <div class="flex items-center gap-2">
            <button
              class="text-action-buttons line-clamp-1 text-left font-bold! text-ellipsis text-blue-50"
              @click="canEdit || isDraft ? emit('showEditVariant') : emit('showVariantDetails')"
            >
              {{ variant.name }}
            </button>
            <span v-if="variant.is_control" class="text-body text-grey-1 shrink-0">Control</span>
          </div>
        </div>
      </div>
    </td>

    <td class="w-24 px-4! py-2! text-right">
      <span class="text-body text-black-2">{{ variant.traffic_percent || 0 }}%</span>
    </td>

    <td v-if="!isInvisible" class="w-32 px-4! py-2! text-right">
      <span class="text-body text-black-2">{{ variant.stats?.impressions || 0 }}</span>
    </td>

    <td class="w-32 px-4! py-2! text-right">
      <span class="text-body text-black-2">{{ variant.stats?.goals || 0 }}</span>
    </td>

    <td class="w-32 px-4! py-2! text-right">
      <span class="text-body text-black-2">{{ conversionValue }}</span>
    </td>

    <td class="w-10 px-0! py-0!">
      <div class="flex items-center justify-center">
        <RfMenu v-if="showDelete" hideIcon preset="promptMenu">
          <template #activator="{ opened }">
            <RfDotsIcon
              class="fill-beige-50 m-auto h-6! w-6! opacity-0 transition-opacity duration-300 ease-in-out group-hover/row:opacity-100!"
              :class="{ 'opacity-100!': opened }"
            />
          </template>
          <template #menu>
            <button
              v-if="showDelete"
              class="flex w-full items-center gap-3 px-4 py-2 last:pb-4!"
              :disabled="disabled"
              @click="emit('showDeleteModal')"
            >
              <RfDeleteIcon class="fill-beige-50 h-6! w-6!" />
              Delete
            </button>
          </template>
        </RfMenu>
      </div>
    </td>
  </tr>
</template>

<script setup>
import { computed } from "vue";
import { useFloatingPreview } from "@/utils/composables/useFloatingPreview";
import {
  isInvisible as isInvisibleHelper,
  isCustomGoalExists,
} from "@/utils/prompts/promptHelpers";
import { percentage } from "@/utils/metricsHelpers";
import { useUserStore } from "@/pinia/userStore";
import RfPromotionThumbnail from "@/components/RfPromotions/RfPromotionThumbnail.vue";
import RfMenu from "@/components/menus/RfMenu.vue";
import RfDotsIcon from "@/components/icons/RfDotsIcon.vue";
import RfDeleteIcon from "@/components/icons/RfDeleteIcon.vue";
import RfEyeOpenIcon from "@/components/icons/RfEyeOpenIcon.vue";

const emit = defineEmits(["showEditVariant", "showDeleteModal", "showVariantDetails"]);

const props = defineProps({
  variant: { type: Object, default: () => ({}) },
  path: { type: Object, default: () => ({}) },
  currExperiment: { type: Object, default: () => ({}) },
  disabled: { type: [Boolean, String], default: false },
});

const userStore = useUserStore();

const {
  thumbnailRef,
  floatingRef,
  isPreviewVisible,
  isExpanded,
  floatingStyle,
  openPreview,
  closePreview,
} = useFloatingPreview();

const isInvisible = computed(() => isInvisibleHelper(props.path));
const hasCustomGoal = computed(() => isCustomGoalExists(props.path));

const conversionValue = computed(() => {
  if (isInvisible.value && !hasCustomGoal.value) return "-";
  const numerator = isInvisible.value
    ? props.variant.stats?.custom_goals_accepted
    : props.variant.stats?.goals;
  const denominator = isInvisible.value
    ? props.variant.stats?.goals
    : props.variant.stats?.impressions;
  return percentage(numerator, denominator, true);
});

const isDraft = computed(
  () => !props.currExperiment.is_active && !props.currExperiment.is_completed,
);
const canEdit = computed(
  () =>
    props.currExperiment.is_active &&
    (userStore.isCurrentSuperAdmin || props.currExperiment.is_live_editable),
);
const showDelete = computed(
  () => isDraft.value && !props.variant.is_control && !props.variant.is_baseline,
);
</script>

<style lang="scss" scoped>
.rf-thumbnail {
  .promo-preview-image {
    min-width: auto;
    width: 100%;
    height: 100%;
  }

  &--overlay {
    transition: opacity 300ms ease-in-out;

    &:hover {
      background: rgb(36 36 36 / 0.6);
    }
  }
}

.rf-floating-preview {
  display: block;
  cursor: pointer;
  transform: scale(1);
  transform-origin: center top;
  transition: transform 300ms cubic-bezier(0.5, 1.2, 0.5, 1.2);

  &--expanded {
    transform: scale(2.5);
  }

  .promo-preview-image {
    min-width: auto;
    width: 100%;
    height: 100%;
  }
}
</style>
